import type { AppType } from '@ballpeek/api/client'
import type { QueryClient } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import { hc } from 'hono/client'
import { createJwtTokenData, JWT_TOKEN_QUERY_KEY } from '~/lib/jwt-utils'
import { resolveBrowserLocalApiUrl } from '~/lib/local-dev-api-url'
import { getQueryClient } from '~/lib/query-client-singleton'
import { getServerRequest } from '~/lib/server-request'
import { SESSION_QUERY_KEY } from '~/lib/session-query'
import { authClient, BETTER_AUTH_URL } from '~/utils/auth'

const API_BASE_URL = resolveBrowserLocalApiUrl(import.meta.env.VITE_API_URL || 'http://localhost:9999')

async function getJwtTokenFromServerRequest(): Promise<string | null> {
  const request = getServerRequest()
  if (!request)
    return null

  const headers = new Headers()
  const cookie = request.headers.get('Cookie')
  if (cookie)
    headers.set('Cookie', cookie)

  const userAgent = request.headers.get('User-Agent')
  if (userAgent)
    headers.set('User-Agent', userAgent)

  const response = await fetch(`${BETTER_AUTH_URL}/token`, {
    method: 'GET',
    headers,
  })

  if (!response.ok)
    return null

  const data = await response.json() as {
    token?: string
    accessToken?: string
    jwt?: string
  } | string

  if (typeof data === 'string')
    return data

  return data.token || data.accessToken || data.jwt || null
}

interface BrowserJwtCache {
  token: string
  expiresAt: number | null
  authContextKey?: string
  authGeneration?: number
}

let browserAuthGeneration = 0
const pendingBrowserJwtTokens = new Map<string, Promise<string | null>>()

function browserAuthContextKey(queryClient: QueryClient): string {
  const sessionData = queryClient.getQueryData(SESSION_QUERY_KEY) as {
    session?: { id?: unknown } | null
    user?: { id?: unknown } | null
  } | undefined
  if (typeof sessionData?.session?.id === 'string') {
    return `session:${sessionData.session.id}`
  }
  if (typeof sessionData?.user?.id === 'string') {
    return `user:${sessionData.user.id}`
  }
  return 'anonymous'
}

function browserAuthContextMatches(
  queryClient: QueryClient,
  generation: number,
  contextKey: string,
): boolean {
  return browserAuthGeneration === generation
    && browserAuthContextKey(queryClient) === contextKey
}

function cacheBrowserJwt(
  queryClient: QueryClient,
  token: string,
  generation: number,
  contextKey: string,
): string | null {
  if (!browserAuthContextMatches(queryClient, generation, contextKey)) {
    return null
  }
  const jwtData = createJwtTokenData(token)
  if (contextKey.startsWith('session:')
    && jwtData.payload?.sessionId !== contextKey.slice('session:'.length)) {
    return null
  }
  if (contextKey.startsWith('user:')
    && jwtData.payload?.id !== contextKey.slice('user:'.length)) {
    return null
  }
  queryClient.setQueryData([JWT_TOKEN_QUERY_KEY], {
    ...jwtData,
    authContextKey: contextKey,
    authGeneration: generation,
  })
  return token
}

export function invalidateBrowserJwtState(): void {
  if (typeof window === 'undefined') {
    return
  }
  browserAuthGeneration += 1
  pendingBrowserJwtTokens.clear()
  getQueryClient().removeQueries({ queryKey: [JWT_TOKEN_QUERY_KEY] })
}

export function invalidateBrowserAuthenticationState(): void {
  if (typeof window === 'undefined') {
    return
  }
  invalidateBrowserJwtState()
  getQueryClient().removeQueries({ queryKey: SESSION_QUERY_KEY })
}

async function getJwtToken(): Promise<string | null> {
  try {
    if (typeof window === 'undefined') {
      return await getJwtTokenFromServerRequest()
    }

    const queryClient = getQueryClient()
    const contextKey = browserAuthContextKey(queryClient)
    const generation = browserAuthGeneration
    const cached = queryClient.getQueryData([JWT_TOKEN_QUERY_KEY]) as BrowserJwtCache | undefined

    if (cached?.token
      && cached.authContextKey === contextKey
      && cached.authGeneration === generation) {
      if (!cached.expiresAt || cached.expiresAt > Date.now()) {
        return cached.token
      }
      queryClient.removeQueries({ queryKey: [JWT_TOKEN_QUERY_KEY] })
    }

    const pendingKey = `${generation}:${contextKey}`
    let tokenRequest = pendingBrowserJwtTokens.get(pendingKey)
    if (!tokenRequest) {
      tokenRequest = (async () => {
        const response = await authClient.token()
        if (!response.error && response.data?.token) {
          return cacheBrowserJwt(
            queryClient,
            response.data.token,
            generation,
            contextKey,
          )
        }

        let jwtFromSession: string | null = null
        const { data: sessionData } = await authClient.getSession({
          fetchOptions: {
            onSuccess: (ctx) => {
              jwtFromSession = ctx.response.headers.get('set-auth-jwt')
            },
          },
        })

        if (!browserAuthContextMatches(queryClient, generation, contextKey)) {
          return null
        }
        if (jwtFromSession) {
          return cacheBrowserJwt(queryClient, jwtFromSession, generation, contextKey)
        }
        if (!sessionData?.session) {
          invalidateBrowserAuthenticationState()
        }
        return null
      })()
      pendingBrowserJwtTokens.set(pendingKey, tokenRequest)
    }

    try {
      return await tokenRequest
    }
    finally {
      if (pendingBrowserJwtTokens.get(pendingKey) === tokenRequest) {
        pendingBrowserJwtTokens.delete(pendingKey)
      }
    }
  }
  catch (error) {
    Sentry.captureException(error, { extra: { source: 'get_jwt_token' } })
    return null
  }
}

export type Client = ReturnType<typeof hc<AppType>>

export function hcWithType(...args: Parameters<typeof hc>): Client {
  return hc<AppType>(...args)
}

async function globalInterceptorFetch(input: RequestInfo | URL, init?: RequestInit) {
  const response = await fetch(input, init)
  if (!response.ok) {
    const apiRequestId = response.headers.get('X-Request-Id')
    Sentry.addBreadcrumb({
      category: 'api_call',
      message: `API request failed: ${response.status}`,
      level: 'error',
      data: { api_request_id: apiRequestId, url: input.toString(), status: response.status },
    })
  }
  return response
}

async function authenticatedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const token = await getJwtToken()

  if (!token) {
    if (typeof window === 'undefined') {
      return new Response(
        JSON.stringify({ error: { issues: [{ code: 'auth_unauthorized', message: 'SSR Unauthorized' }] } }),
        { status: 401, headers: { 'Content-Type': 'application/json' } },
      )
    }
    throw new Error('Unauthorized: No valid JWT token available.')
  }

  const headers = new Headers(init?.headers)
  headers.set('Authorization', `Bearer ${token}`)

  const response = await globalInterceptorFetch(input, {
    ...init,
    headers,
  })
  if (response.status === 401 && typeof window !== 'undefined') {
    const queryClient = getQueryClient()
    const cached = queryClient.getQueryData([JWT_TOKEN_QUERY_KEY]) as BrowserJwtCache | undefined
    if (cached?.token === token) {
      queryClient.removeQueries({ queryKey: [JWT_TOKEN_QUERY_KEY] })
    }
  }
  return response
}

export function createAuthenticatedFetch(signal?: AbortSignal) {
  if (!signal) {
    return authenticatedFetch
  }
  return (input: RequestInfo | URL, init?: RequestInit) =>
    authenticatedFetch(input, {
      ...init,
      signal,
    })
}

export const apiClient = hcWithType(API_BASE_URL, {
  fetch: globalInterceptorFetch,
})

export const authApiClient = hcWithType(API_BASE_URL, {
  fetch: createAuthenticatedFetch(),
})

export function getApiBaseUrl() {
  return API_BASE_URL
}
